// src/pages/Users/CreateUserModal.jsx
import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../../components/UI/Modal';
import Input from '../../components/UI/Input';
import { useSelector, useDispatch } from 'react-redux';
import { getAllItems, getRegions } from '../../actions';
import { firstNames, lastNames, orgNames, roleOptions } from '../../store/localItems';

const randItem = (arr) => arr[Math.floor(Math.random() * arr.length)];
const randDigits = (n) => Array.from({ length: n }, () => Math.floor(Math.random() * 10)).join('');
const slugify = (s) => String(s).toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9\-]/g, '');

const generateStrongPassword = (len = 12) => {
    const L = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const l = 'abcdefghijkmnopqrstuvwxyz';
    const d = '23456789';
    const s = '!@#$%^&*?';
    const all = L + l + d + s;
    const pick = (pool) => pool[Math.floor(Math.random() * pool.length)];
    let pwd = pick(L) + pick(l) + pick(d) + pick(s);
    for (let i = pwd.length; i < len; i++) pwd += pick(all);
    return pwd.split('').sort(() => Math.random() - 0.5).join('');
};

const initialForm = {
    firstName: '',
    lastName: '',
    email: '',
    contactNumber: '',
    role: 'user',
    region: '',
    regionName: '',
    nameOrganization: '',
    addStandardSubs: false,
    subscriptions: [],
    // пароль
    useManualPassword: false,
    password: '',
    password2: '',
};

export default function CreateUserModal({ show, handleClose, onSubmit }) {
    const dispatch = useDispatch();

    const regionSlice = useSelector(s => s.region);
    const regionsRaw = regionSlice?.regions || regionSlice?.items || regionSlice?.list || [];

    const { mainPages } = useSelector(s => s.mainPage);

    const regionOptions = useMemo(() => {
        if (!Array.isArray(regionsRaw)) return [];
        return regionsRaw
            .map(r => ({
                value: r?._id ?? r?.value ?? r?.id,
                name: r?.name ?? r?.label ?? r?.title ?? 'Без названия',
            }))
            .filter(o => o.value && o.name);
    }, [regionsRaw]);

    const [form, setForm] = useState(initialForm);
    const [touched, setTouched] = useState(false);
    const [openSubsPicker, setOpenSubsPicker] = useState(false);
    const [showPwd, setShowPwd] = useState(false);
    const [showPwd2, setShowPwd2] = useState(false);

    useEffect(() => { dispatch(getRegions()); }, [dispatch]);
    useEffect(() => { dispatch(getAllItems('mainPage', '_id,name')); }, [dispatch]);

    useEffect(() => {
        if (!show) return;
        const firstRegion = regionOptions[0];
        setForm({
            ...initialForm,
            region: firstRegion?.value || '',
            regionName: firstRegion?.name || '',
        });
        setTouched(false);
        setOpenSubsPicker(false);
        setShowPwd(false);
        setShowPwd2(false);
    }, [show, regionOptions]);

    // автозаполнение подписок, если стоит флаг
    useEffect(() => {
        if (!form.addStandardSubs) return;
        if (!Array.isArray(mainPages) || mainPages.length === 0) return;
        setForm(prev => ({
            ...prev,
            subscriptions: mainPages.map(mp => ({ mainPage: mp._id, isCompleted: true })),
        }));
    }, [form.addStandardSubs, mainPages]);

    const generateAll = () => {
        const first = randItem(firstNames);
        const last = randItem(lastNames);
        const org = randItem(orgNames);
        const base = slugify(`${first}.${last}`) || 'user';
        const email = `${base}${randDigits(2)}@example.com`;
        const phone = `+79${randDigits(9)}`;
        const firstRegion = regionOptions[0];

        setForm(f => ({
            ...f,
            firstName: first,
            lastName: last,
            email,
            contactNumber: phone,
            nameOrganization: org,
            region: firstRegion?.value || '',
            regionName: firstRegion?.name || '',
            role: 'user',
        }));
        setTouched(true);
    };

    const onChange = (name, value) => {
        setForm(prev => ({ ...prev, [name]: value }));
        if (!touched) setTouched(true);
    };

    const onChangeRegion = (value) => {
        const opt = regionOptions.find(o => String(o.value) === String(value));
        setForm(prev => ({ ...prev, region: value, regionName: opt?.name || '' }));
        if (!touched) setTouched(true);
    };

    // ручная настройка подписок
    const isChecked = (id) => form.subscriptions.some(s => String(s.mainPage) === String(id) && s.isCompleted);
    const toggleSub = (id) => {
        setForm(prev => {
            const exists = prev.subscriptions.find(s => String(s.mainPage) === String(id));
            let next;
            if (!exists) next = [...prev.subscriptions, { mainPage: id, isCompleted: true }];
            else next = prev.subscriptions.map(s => String(s.mainPage) === String(id) ? { ...s, isCompleted: !s.isCompleted } : s);
            return { ...prev, subscriptions: next };
        });
    };

    // валидация
    const emailOk = /\S+@\S+\.\S+/.test(form.email);
    const passwordOk = !form.useManualPassword
        || (
            typeof form.password === 'string'
            && form.password.length >= 6
            && form.password === form.password2
        );

    const requiredOk =
        form.firstName.trim()
        && form.lastName.trim()
        && emailOk
        && form.role
        && form.region
        && form.regionName
        && passwordOk;

    const disabledSubmit = !requiredOk;

    const submit = () => {
        if (disabledSubmit) return;
        const payload = {
            ...form,
            // если пароль руками не задан — не отправляем поле вовсе
            password: form.useManualPassword ? form.password : undefined,
        };
        onSubmit?.(payload);
    };

    return (
        <Modal
            show={show}
            handleClose={handleClose}
            modalTitle="Создать пользователя"
            onSubmit={submit}
            disabledSubmit={disabledSubmit}
            confirmText="Создать"
            closeText="Отмена"
        >
            <div style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'center' }}>
                <button type="button" className="btn btn-outline-dark btn-sm" onClick={generateAll}>
                    Сгенерировать данные
                </button>

                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input
                        type="checkbox"
                        checked={form.addStandardSubs}
                        onChange={e => onChange('addStandardSubs', e.target.checked)}
                    />
                    Добавить стандартные подписки
                </label>

                <button
                    type="button"
                    className="btn btn-light btn-sm"
                    onClick={() => setOpenSubsPicker(v => !v)}
                    disabled={!Array.isArray(mainPages) || mainPages.length === 0}
                    title="Выбрать вручную из списка страниц"
                >
                    {openSubsPicker ? 'Скрыть список' : 'Настроить подписки'}
                </button>
            </div>

            {openSubsPicker && Array.isArray(mainPages) && mainPages.length > 0 && (
                <div style={{ border: '1px solid #eee', borderRadius: 8, padding: 10, marginBottom: 14, maxHeight: 220, overflow: 'auto' }}>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Подписки:</div>
                    {mainPages.map(mp => (
                        <label key={mp._id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <input type="checkbox" checked={isChecked(mp._id)} onChange={() => toggleSub(mp._id)} />
                            <span>{mp.name || mp.title || mp.slug || mp._id}</span>
                        </label>
                    ))}
                    <small className="text-muted">Отмеченные пункты уйдут как {'{ mainPage, isCompleted }'}.</small>
                </div>
            )}

            {/* Основные поля */}
            <div className="row">
                <div className="col-md-6">
                    <Input
                        showError
                        label="Имя*"
                        name="firstName"
                        value={form.firstName}
                        placeholder="Имя"
                        onChange={e => onChange('firstName', e.target.value)}
                    />
                </div>
                <div className="col-md-6">
                    <Input
                        showError
                        label="Фамилия*"
                        name="lastName"
                        value={form.lastName}
                        placeholder="Фамилия"
                        onChange={e => onChange('lastName', e.target.value)}
                    />
                </div>
            </div>

            <div className="row">
                <div className="col-md-8">
                    <Input
                        showError
                        label="Email*"
                        name="email"
                        value={form.email}
                        placeholder="mail@example.com"
                        onChange={e => onChange('email', e.target.value)}
                    />
                    {!emailOk && touched ? <small style={{ color: '#c00' }}>Некорректный email</small> : null}
                </div>
                <div className="col-md-4">
                    <Input
                        label="Телефон"
                        name="contactNumber"
                        value={form.contactNumber}
                        placeholder="+79991234567"
                        onChange={e => onChange('contactNumber', e.target.value)}
                    />
                </div>
            </div>

            {/* Пароль: ручной режим */}
            <div style={{ marginTop: 8, marginBottom: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input
                        type="checkbox"
                        checked={form.useManualPassword}
                        onChange={e => onChange('useManualPassword', e.target.checked)}
                    />
                    Задать пароль вручную
                </label>
            </div>

            {form.useManualPassword && (
                <>
                    <div className="row">
                        <div className="col-md-6">
                            <Input
                                type={showPwd ? 'text' : 'password'}
                                showError
                                label="Пароль* (≥ 6 символов)"
                                name="password"
                                value={form.password}
                                placeholder="Введите пароль"
                                onChange={e => onChange('password', e.target.value)}
                            />
                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                                <input type="checkbox" checked={showPwd} onChange={() => setShowPwd(v => !v)} />
                                Показать пароль
                            </label>
                        </div>
                        <div className="col-md-6">
                            <Input
                                type={showPwd2 ? 'text' : 'password'}
                                showError
                                label="Повтор пароля*"
                                name="password2"
                                value={form.password2}
                                placeholder="Повторите пароль"
                                onChange={e => onChange('password2', e.target.value)}
                            />
                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                                <input type="checkbox" checked={showPwd2} onChange={() => setShowPwd2(v => !v)} />
                                Показать повтор
                            </label>
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 6, marginBottom: 6 }}>
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => onChange('password', generateStrongPassword(12))}
                        >
                            Сгенерировать сильный пароль
                        </button>
                        {!passwordOk && (
                            <small style={{ color: '#c00', alignSelf: 'center' }}>
                                Пароль короче 6 символов или не совпадает.
                            </small>
                        )}
                    </div>
                </>
            )}

            <div className="row">
                <div className="col-md-6">
                    <Input
                        type="select"
                        showError
                        label="Роль*"
                        name="role"
                        value={form.role}
                        onChange={e => onChange('role', e.target.value)}
                        options={roleOptions}
                    />
                </div>
                <div className="col-md-6">
                    <Input
                        type="select"
                        showError
                        label="Регион*"
                        name="region"
                        value={form.region}
                        onChange={e => onChangeRegion(e.target.value)}
                        options={regionOptions}
                        placeholder="Выбрать регион"
                    />
                    <small className="text-muted">Название региона подставится автоматически.</small>
                </div>
            </div>

            <div className="row">
                <div className="col-md-6">
                    <Input
                        label="Название организации"
                        name="nameOrganization"
                        value={form.nameOrganization}
                        placeholder='например, ООО "Альфа"'
                        onChange={e => onChange('nameOrganization', e.target.value)}
                    />
                </div>
            </div>
        </Modal>
    );
}
